import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { constants } from "@qgiv/core-js";
import { StartAnalyticsEvents } from "@qgiv/core-donor";
import {
    getClosedAnalyticsEvents,
    getDataLayerOptions,
    sendStartAnalyticsEvents,
} from "@qgiv/p2p-registration";
import { selectCurrentPage } from "../../redux/slices/currentPageSlice";
import { selectAllClassifications } from "../../redux/slices/classificationsSlice";
import {
    selectAppSettings,
    setAppHasLoadedFirstRegistrationPage,
} from "../../redux/slices/appSettingsSlice";
import { selectAllFormSettings } from "../../redux/slices/formSettingsSlice";
import { selectAllP2pSettings } from "../../redux/slices/p2pSettingsSlice";
import { selectAllRegistration } from "../../redux/slices/registrationSlice";
import { selectReceipt } from "../../redux/slices/receiptSlice";

const ConnectedStartAnalyticsEvents = () => {
    const {
        ENUMS: {
            DefaultView: {
                REGISTRATION_CLASSIFICATION_STEP,
                REGISTRATION_DETAILS_STEP,
            },
        },
    } = constants;
    const appSettings = useSelector(selectAppSettings);
    const formSettings = useSelector(selectAllFormSettings);
    const registration = useSelector(selectAllRegistration);
    const currentPage = useSelector(selectCurrentPage);
    const receipt = useSelector(selectReceipt);
    const classifications = useSelector(selectAllClassifications);
    const p2pSettings = useSelector(selectAllP2pSettings);
    const { hasSubmittedRegistration, hasLoadedFirstRegistrationPage } =
        appSettings;
    const { enableClassifications } = p2pSettings;
    const dispatch = useDispatch();

    // Generate all of the data that can be sent with payload
    const dataLayerOptions = getDataLayerOptions({
        formSettings,
        receipt,
        registration,
    });

    // Generate flag that ensures we only run start events once
    // Should only run once when registration starts. Because currentPage is
    // set to 0 on page load we have to monitor the currentPage value and wait
    // for it to update after load. However, because the donor can visit pages
    // other than the ones that start a registration directly (e.g. the receipt
    // page) we only want to send the start events if the donor is actually
    // taken to a page where they can start registering for a P2P event. And
    // once they are taken to that page we know that we no longer need to run
    // the start events and we can start running the change events instead.
    const hasClassifications =
        enableClassifications && classifications.length > 0;

    const isPageWhereRegistrationStarts = hasClassifications
        ? currentPage === REGISTRATION_CLASSIFICATION_STEP
        : currentPage === REGISTRATION_DETAILS_STEP;

    const shouldRunStartAnalyticsEvents =
        !hasSubmittedRegistration &&
        !hasLoadedFirstRegistrationPage &&
        isPageWhereRegistrationStarts;

    const hasSubmittedForm = hasSubmittedRegistration;

    const setHasSentStartAnalyticsEventsValue = () => {
        dispatch(setAppHasLoadedFirstRegistrationPage(true));
    };

    const startAnalyticsEventsProps = {
        currentPage,
        dataLayerOptions,
        getClosedAnalyticsEvents,
        hasSubmittedForm,
        sendStartAnalyticsEvents,
        setHasSentStartAnalyticsEventsValue,
        shouldRunStartAnalyticsEvents,
    };

    return <StartAnalyticsEvents {...startAnalyticsEventsProps} />;
};

export default ConnectedStartAnalyticsEvents;
